import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ALL_PERMISSIONS, PERMISSIONS, type Permission } from '@wms/shared';
import { apiFetch, ApiError } from '../../lib/api';
import { useElevatedFetch } from '../../lib/elevation';
import { permissionLabel } from './permission-picker';

interface UserRoleRow {
  readonly userRoleId: string;
  readonly roleId: string;
  readonly roleCode: string;
  readonly roleNameKey: string;
  readonly warehouseId: string | null;
}
interface OverrideRow {
  readonly id: string;
  readonly permissionCode: string;
  readonly warehouseId: string | null;
  readonly effect: 'GRANT' | 'DENY';
  readonly reason: string | null;
}
interface UserDetail {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly status: 'ACTIVE' | 'SUSPENDED';
  readonly roles: UserRoleRow[];
  readonly overrides: OverrideRow[];
  readonly warehouses: { warehouseId: string; grantedAt: string }[];
}
interface RoleRow {
  readonly id: string;
  readonly code: string;
  readonly nameKey: string;
}

export function UserDetailPage(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const elevatedFetch = useElevatedFetch();

  const detailQuery = useQuery({
    queryKey: ['users', userId],
    queryFn: () => apiFetch<UserDetail>(`/users/${userId}`),
  });
  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiFetch<{ items: RoleRow[] }>('/roles'),
  });
  const effectiveQuery = useQuery({
    queryKey: ['users', userId, 'effective-permissions'],
    queryFn: () => apiFetch<{ permissions: string[] }>(`/users/${userId}/effective-permissions`),
  });

  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [overrideCode, setOverrideCode] = useState<Permission>(PERMISSIONS.INVENTORY_VIEW);
  const [overrideEffect, setOverrideEffect] = useState<'GRANT' | 'DENY'>('GRANT');
  const [error, setError] = useState<string | null>(null);

  const invalidateAll = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['users', userId] }),
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'effective-permissions'] }),
    ]);
  };

  const runElevated = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await fn();
      await invalidateAll();
    } catch (err) {
      if (!(err instanceof Error && err.message === 'elevation_cancelled')) {
        setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      }
    }
  };

  if (!detailQuery.data) return <p>{t('app.loading')}</p>;
  const user = detailQuery.data;

  return (
    <div>
      <h1 style={{ fontSize: 'var(--text-2xl)' }}>{user.displayName}</h1>
      <p className="field-hint">{user.email}</p>
      {error && <p className="field-error">{error}</p>}

      <section className="section">
        <h2>{t('users.detailTabRoles')}</h2>
        <div className="table-scroll"><table className="table">
          <thead>
            <tr>
              <th>{t('users.colRoles')}</th>
              <th>{t('users.selectWarehouseOptional')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {user.roles.map((r) => (
              <tr key={r.userRoleId}>
                <td>{t(r.roleNameKey, r.roleCode)}</td>
                <td>{r.warehouseId ?? t('users.roleGlobal')}</td>
                <td>
                  <button
                    className="btn btn--danger btn--sm"
                    onClick={() =>
                      void runElevated(() =>
                        elevatedFetch(
                          PERMISSIONS.USER_MANAGE,
                          `/users/${userId}/roles/${r.userRoleId}`,
                          {},
                          'DELETE',
                        ),
                      )
                    }
                  >
                    {t('users.revokeRole')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="row" style={{ marginTop: 'var(--space-3)' }}>
          <select value={selectedRoleId} onChange={(e) => setSelectedRoleId(e.target.value)}>
            <option value="">{t('users.selectRole')}</option>
            {rolesQuery.data?.items.map((r) => (
              <option key={r.id} value={r.id}>
                {t(r.nameKey, r.code)}
              </option>
            ))}
          </select>
          <button
            className="btn btn--ghost"
            disabled={!selectedRoleId}
            onClick={() =>
              void runElevated(() =>
                elevatedFetch(PERMISSIONS.USER_MANAGE, `/users/${userId}/roles`, {
                  roleId: selectedRoleId,
                }),
              )
            }
          >
            {t('users.assignRole')}
          </button>
        </div>
      </section>

      <section className="section">
        <h2>{t('users.detailTabOverrides')}</h2>
        <div className="table-scroll"><table className="table">
          <thead>
            <tr>
              <th>{t('users.colRoles')}</th>
              <th>
                {t('users.overrideGrant')}/{t('users.overrideDeny')}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {user.overrides.map((o) => (
              <tr key={o.id}>
                <td title={o.permissionCode}>{permissionLabel(t, o.permissionCode)}</td>
                <td>
                  <span
                    className={`badge ${o.effect === 'GRANT' ? 'badge--success' : 'badge--danger'}`}
                  >
                    {t(o.effect === 'GRANT' ? 'users.overrideGrant' : 'users.overrideDeny')}
                  </span>
                </td>
                <td>
                  <button
                    className="btn btn--danger btn--sm"
                    onClick={() =>
                      void runElevated(() =>
                        elevatedFetch(
                          PERMISSIONS.USER_MANAGE,
                          `/users/${userId}/overrides/${o.id}`,
                          {},
                          'DELETE',
                        ),
                      )
                    }
                  >
                    {t('users.removeOverride')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="row" style={{ marginTop: 'var(--space-3)' }}>
          <select
            value={overrideCode}
            onChange={(e) => setOverrideCode(e.target.value as Permission)}
          >
            {ALL_PERMISSIONS.map((p) => (
              <option key={p} value={p}>
                {permissionLabel(t, p)}
              </option>
            ))}
          </select>
          <select
            value={overrideEffect}
            onChange={(e) => setOverrideEffect(e.target.value as 'GRANT' | 'DENY')}
          >
            <option value="GRANT">{t('users.overrideGrant')}</option>
            <option value="DENY">{t('users.overrideDeny')}</option>
          </select>
          <button
            className="btn btn--ghost"
            onClick={() =>
              void runElevated(() =>
                elevatedFetch(PERMISSIONS.USER_MANAGE, `/users/${userId}/overrides`, {
                  permissionCode: overrideCode,
                  effect: overrideEffect,
                }),
              )
            }
          >
            {t('users.addOverride')}
          </button>
        </div>
      </section>

      <section className="section">
        <h2>{t('users.effectivePermissionsTitle')}</h2>
        <p className="field-hint">{t('users.effectivePermissionsHint')}</p>
        <div className="permission-matrix">
          {effectiveQuery.data?.permissions.map((p) => (
            <span key={p} className="badge badge--success" title={p}>
              {permissionLabel(t, p)}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
